import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, SearchX } from 'lucide-react';

export type ErrorStateVariant = 'not-found' | 'load-failed';

interface ErrorStateProps {
  variant: ErrorStateVariant;
  /** Override the default i18n title (`common.notFound.title` / `common.loadFailed.title`). */
  title?: ReactNode;
  /** Override the default i18n body. */
  body?: ReactNode;
  /** Exit CTA(s) — back-to-list link, retry button, etc. Never a dead end. */
  cta?: ReactNode;
}

/**
 * EmptyState's error sibling (F064): friendly pt copy + an exit CTA for
 * not-found (404) and load-failed (network/5xx) situations. Pages must NEVER
 * render the server's raw `error.message` — pass nothing and let the variant
 * defaults speak, or pass owner-friendly i18n strings.
 */
export function ErrorState({ variant, title, body, cta }: ErrorStateProps) {
  const { t } = useTranslation();
  const Icon = variant === 'not-found' ? SearchX : AlertTriangle;
  const baseKey = variant === 'not-found' ? 'common.notFound' : 'common.loadFailed';

  return (
    <div
      data-testid={`error-state-${variant}`}
      role={variant === 'load-failed' ? 'alert' : undefined}
      className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-12 text-center"
    >
      <div className="rounded-full bg-muted p-3">
        <Icon className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
      </div>
      <h3 className="text-base font-medium">{title ?? t(`${baseKey}.title`)}</h3>
      <p className="max-w-sm text-sm text-muted-foreground">{body ?? t(`${baseKey}.body`)}</p>
      {cta ? (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">{cta}</div>
      ) : null}
    </div>
  );
}
